import { PageLayoutHostProvider } from '@evisort/eds/components/PageLayout/PageLayout';
import { lazy, ReactNode, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router';

import type { User } from '~/types/User';

import { useAuthenticationStore } from '~/auth/useAuthenticationStore';
import { AppShell } from '~/common/layout/AppShell';
import { AppShellSlotProvider } from '~/common/layout/AppShellSlotRegistry';
import { AppShellHeader } from '~/common/layout/AppShellSlots';
import {
  componentConfig,
  djsConfig,
} from '~/components/App/AppContents/AppContents.config';
import { MAIN_CONTENT_ID } from '~/components/App/AppContents/constants';
import { AppData } from '~/components/App/AppData';
import SplashPage from '~/components/SplashPage/SplashPage';
import { LoadingSpinner } from '~/eds/src/components/LoadingSpinner/LoadingSpinner';
import {
  AppBreadcrumb,
  AppBreadcrumbTrail,
} from '~/features/app/AppBreadcrumb';
import { AppBreadcrumbSlotProvider } from '~/features/app/AppBreadcrumbSlotContext';
import { AppPageBanner } from '~/features/app/AppPageBanner';
import { AppPageNavbar } from '~/features/app/AppPageNavbar';
import { AppSidebarNav } from '~/features/app/AppSidebarNav';
import { useSidebarNav } from '~/features/app/SidebarNavContext';
import { SidebarNavOnboardingModal } from '~/features/app/SidebarNavOnboardingModal';
import { useHasLegacySubNav } from '~/features/app/useHasLegacySubNav';
import { RouteLayout, useRouteLayout } from '~/features/app/useRouteLayout';
import { AskAiProvider } from '~/features/ask-anything/contexts/AskAiContext';
import { NotificationCenterProvider } from '~/features/notifications/NotificationCenterContext';
import { AiWorkflowBuildsNotificationSource } from '~/features/workflows-clm-evolution/ai-builds/AiWorkflowBuildsNotificationSource';
import { RedirectToXRayModal } from '~/features/x-ray/modals/RedirectToXRayModal';
import { useAreFlagsIdentifiedForClient } from '~/flags/useFlagsReady';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { MetricsService } from '~/services/MetricsService/MetricsService';

import './LoggedInLayout.css';

const UploadInfoPanel = lazy(
  () => import('~/components/Shared/UploadInfoPanel/UploadInfoPanel'),
);
const DropzoneComponent = lazy(() => import('react-dropzone-component'));
const ChatBotPanel = lazy(() =>
  import('~/features/ask-anything/chat-bot-panel/ChatBotPanel').then((m) => ({
    default: m.ChatBotPanel,
  })),
);

interface RouteContentProps {
  layout: RouteLayout;
  hasSubNav: boolean;
  banner?: ReactNode;
  breadcrumbs?: ReactNode;
  framed?: boolean;
  sideNav?: ReactNode;
  topNav?: ReactNode;
}

function RouteContent({
  layout,
  hasSubNav,
  banner,
  breadcrumbs,
  framed,
  sideNav,
  topNav,
}: RouteContentProps) {
  return (
    <AppShellSlotProvider>
      <PageLayoutHostProvider headerSlot={AppShellHeader}>
        <AppShell
          banner={banner}
          breadcrumbs={breadcrumbs}
          framed={framed}
          hasSubNav={hasSubNav}
          layout={layout}
          mainId={MAIN_CONTENT_ID}
          sideNav={sideNav}
          topNav={topNav}
        >
          <Outlet />
        </AppShell>
      </PageLayoutHostProvider>
    </AppShellSlotProvider>
  );
}

function LegacyNavRouteContent({ layout }: { layout: RouteLayout }) {
  const hasSubNav = useHasLegacySubNav();
  return (
    <RouteContent
      framed={false}
      hasSubNav={hasSubNav}
      layout={layout}
      topNav={<AppPageNavbar />}
    />
  );
}

/**
 * Layout route for authenticated app pages. Owns the logged-in chrome
 * (navbar, upload panel, auth-required modal, dropzone, per-page padding)
 * and gates access — unauthenticated visitors get redirected to /login.
 *
 * Renders one of two nav shells around the routed content: the legacy top
 * navbar, or the vertical sidebar nav when `isSidebarLayoutActive`
 * (`SidebarNavContext`).
 *
 * Replaces the auth-conditional rendering that used to live in
 * `AppContents` and the bespoke pathname-switching in `PrivateRoute`.
 */
export function LoggedInLayout() {
  const location = useLocation();
  const {
    isLoggedIn: isUserLoggedIn,
    requiresReauth,
    hasPendingClientSwitchConfirmation,
  } = useAuthenticationStore();
  const currentUser = useSelector(
    slices.currentUser.selectors.selectCurrentUser,
  );
  const user: User | undefined =
    Object.keys(currentUser).length > 0 ? (currentUser as User) : undefined;

  const areFlagsIdentified = useAreFlagsIdentifiedForClient(user?.client);

  const { isSidebarLayoutActive: useSidebarLayout, isSidebarLayoutResolving } =
    useSidebarNav();
  const layout = useRouteLayout();
  const usesChromelessLayout = layout === 'chromeless';

  useEffect(() => {
    if (isUserLoggedIn) {
      MetricsService.appReady();
    }
  }, [isUserLoggedIn]);

  if (!isUserLoggedIn) {
    // Keep the current URL mounted (rendering nothing) while the auth-required
    // modal owned by AppContents handles re-auth. Skip on
    // /folders-permission-check, where the modal itself is suppressed
    // (EKP-24875).
    if (
      (requiresReauth || hasPendingClientSwitchConfirmation) &&
      !location.pathname.includes('folders-permission-check')
    ) {
      return null;
    }
    return (
      <Navigate to={RoutePathType.Login} state={{ from: location }} replace />
    );
  }

  if (!areFlagsIdentified) {
    return <SplashPage />;
  }

  // Chrome-independent app globals, mounted the same way under either nav
  // shell so route content behaves identically regardless of the layout.
  // Upload/dropzone/chat are skipped on chromeless pages (e.g. the esign
  // redirect tab), which have no upload or chat-support UI to justify
  // pulling in their chunks and competing with that page's own critical path.
  const globals = (
    <>
      <AppData />
      <AiWorkflowBuildsNotificationSource />
      {!usesChromelessLayout && (
        <>
          {user && (
            <Suspense fallback={null}>
              {/* `onRef` is an intentional no-op: UploadInfoPanel's class-component
                  API calls `props.onRef(this)` unconditionally, but nothing ever
                  read the ref. Drop-in equivalent until UploadInfoPanel migrates
                  to TSX under EKP-47979. */}
              <UploadInfoPanel user={user} onRef={() => undefined} />
            </Suspense>
          )}
          <Suspense fallback={null}>
            <DropzoneComponent
              className="app-dropzone"
              config={componentConfig}
              djsConfig={djsConfig}
            />
          </Suspense>
          <Suspense fallback={null}>
            <ChatBotPanel />
          </Suspense>
          <RedirectToXRayModal />
        </>
      )}
    </>
  );

  let content;
  if (usesChromelessLayout) {
    content = (
      <main id={MAIN_CONTENT_ID}>
        <Outlet />
      </main>
    );
  } else if (isSidebarLayoutResolving) {
    content = (
      <div className="app-shell-layout__resolving">
        <LoadingSpinner />
      </div>
    );
  } else if (useSidebarLayout) {
    content = (
      <AppBreadcrumbSlotProvider>
        <RouteContent
          banner={<AppPageBanner />}
          breadcrumbs={<AppBreadcrumbTrail />}
          hasSubNav={false}
          layout={layout}
          sideNav={
            <>
              <AppSidebarNav />
              <SidebarNavOnboardingModal />
            </>
          }
          topNav={<AppBreadcrumb />}
        />
      </AppBreadcrumbSlotProvider>
    );
  } else {
    content = <LegacyNavRouteContent layout={layout} />;
  }

  return (
    <AskAiProvider>
      <NotificationCenterProvider>
        {content}
        {globals}
      </NotificationCenterProvider>
    </AskAiProvider>
  );
}
